import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from './api-client.service';
import { LocalePreferenceService } from './locale-preference.service';
import { I18nService } from './i18n.service';
import { SiteConfigService } from './site-config.service';

export interface CartVisualItem {
  cartID?: number;
  sellerID?: number;
  sellerName?: string;
  shippingID?: number;
  shippingName?: string;
  listingID: number;
  slug?: string;
  versionID?: number;
  formAnswerID?: number;
  listingType?: string;
  title: string;
  imageUrl: string | null;
  quantity: number;
  price?: number;
  originalPrice?: number;
  currency?: string;
  options?: Array<{ name: string; value: string; unit?: string }>;
  selectedOptions?: Record<number, number>;
  customization?: Record<string, unknown>;
  preparationDays?: number;
  campaign?: { id?: number; title?: string; campaignType?: string; discountType?: string | null; discountValue?: number | null } | null;
}

export interface SavedCartItem {
  id: number;
  listingID?: number;
  title: string;
  slug?: string;
  imageUrl?: string | null;
}

export interface CommerceCartSnapshot {
  items: CartVisualItem[];
  country: string;
  groups: Array<{ sellerID: number; sellerName: string; shippingID: number; shippingName: string; shippingProfileRouteID?:number; selectedShippingID:number; preparationDays:number; shippingOptions:Array<{id:number;name:string;price:number;addPrice:number;minDay:number;maxDay:number;minShipping:number;maxShipping:number;shippingPeriod:string}>; items: CartVisualItem[] }>;
  prices: Record<string, unknown>;
  availableCoupons: Array<{ id: number; title: string; code: string; discountType?: string | null; discountValue?: number | null; maxDiscount?: number | null }>;
  appliedCouponCode?: string;
}

@Injectable({ providedIn: 'root' })
export class CommerceCartService {
  private readonly api = inject(ApiClientService);
  private readonly localePreference = inject(LocalePreferenceService);
  private readonly i18n = inject(I18nService);
  private readonly site = inject(SiteConfigService);
  readonly items = signal<CartVisualItem[]>(this.restore());
  readonly count = computed(() => this.items().reduce((total, item) => total + item.quantity, 0));
  readonly lastAdded = signal<CartVisualItem | null>(null);
  readonly couponCode = signal('');

  async load(): Promise<CommerceCartSnapshot> {
    // Shipping eligibility is country-dependent. Make sure the persisted or
    // server-provided country is available before ApiClientService builds the
    // locale headers for cartList.
    await this.localePreference.load();
    const country = this.localePreference.country() || this.i18n.country();
    const currency = this.localePreference.currency();
    const response = await firstValueFrom(this.api.post<Record<string, unknown>>('cartList', {
      country,
      currency,
      couponCode: this.couponCode() || undefined
    }));
    const canonicalData = response['data'] && typeof response['data'] === 'object'
      ? response['data'] as Record<string, unknown>
      : null;
    const apiData = this.findObject(response, 'apiData') ?? response;
    const cartEnvelope = (canonicalData?.['cart'] ?? apiData['cartList'] ?? this.findValue(response, 'cartList') ?? {}) as Record<string, unknown>;
    // cartList returns a cart summary envelope: { cartList, seller, payment }.
    // Unwrap it before iterating sellers; otherwise the client falls back to
    // its local visual cart and all calculated amounts appear as zero.
    const rawCart = (cartEnvelope['cartList'] && typeof cartEnvelope['cartList'] === 'object'
      ? cartEnvelope['cartList']
      : cartEnvelope) as Record<string, unknown>;
    const rawPrices = (canonicalData?.['payment'] ?? apiData['cartPrices'] ?? apiData['payment'] ?? cartEnvelope['payment'] ?? this.findValue(response, 'cartPrices') ?? this.findValue(response, 'payment') ?? {}) as Record<string, unknown>;
    const prices: Record<string, unknown> = {
      ...rawPrices,
      listingPrice: rawPrices['listingPrice'] ?? rawPrices['totalLastPrice'] ?? rawPrices['totalPrice'] ?? 0,
      shippingCost: rawPrices['shippingCost'] ?? rawPrices['totalShippingPrice'] ?? rawPrices['totalShippintLastPrice'] ?? 0,
      totalAmount: rawPrices['totalAmount'] ?? rawPrices['paymentAmount'] ?? rawPrices['totalLastPrice'] ?? 0
    };
    const groups: CommerceCartSnapshot['groups'] = [];
    for (const [sellerKey, sellerValue] of Object.entries(rawCart || {})) {
      if (!sellerValue || typeof sellerValue !== 'object') continue;
      const sellerRecord=sellerValue as Record<string,unknown>;
      const sellerInfo=sellerRecord['sellerInfo']&&typeof sellerRecord['sellerInfo']==='object'?sellerRecord['sellerInfo'] as Record<string,unknown>:{};
      const shippingRecord=sellerRecord['cartSeller']&&typeof sellerRecord['cartSeller']==='object'?sellerRecord['cartSeller'] as Record<string,unknown>:sellerRecord;
      for (const [shippingKey, shippingValue] of Object.entries(shippingRecord)) {
        if (!shippingValue || typeof shippingValue !== 'object') continue;
        const group = shippingValue as Record<string, unknown>;
        const listings = Array.isArray(group['listings']) ? group['listings'] as Array<Record<string, unknown>> : [];
        if (!listings.length) continue;
        const shippingProfiles=this.asRecordList(group['shippingProfiles']);
        const selectedRouteID=Number(group['shippingProfileRouteID'] ?? group['selectedShippingID'] ?? group['currentSelectedShippingID'] ?? 0);
        const selectedProfile=shippingProfiles.find(option=>Number(option['shippingProfileRouteID'] ?? option['id'] ?? 0)===selectedRouteID)
          ?? shippingProfiles.find(option=>['1','true','yes','on'].includes(String(option['choosed'] ?? option['selected'] ?? '').toLowerCase()));
        groups.push({
          sellerID: Number(sellerKey),
          sellerName: String(sellerInfo['shopName'] ?? sellerInfo['sellerName'] ?? group['sellerName'] ?? group['shopName'] ?? `Mağaza #${sellerKey}`),
          shippingID: Number(shippingKey),
          shippingName: String(group['shippingName'] ?? group['shippingTitle'] ?? `Gönderim profili #${shippingKey}`),
          shippingProfileRouteID:selectedRouteID || undefined,
          selectedShippingID:Number(selectedRouteID || (selectedProfile?.['shippingProfileRouteID'] ?? selectedProfile?.['id'] ?? 0)),
          shippingOptions:shippingProfiles.map(option=>({id:Number(option['shippingProfileRouteID']??option['id']??0),name:String(option['name']??option['title']??'Kargo'),price:this.firstPositive([option['totalAmount'],option['exchangeTotalAmount'],option['shippingPrice'],option['price'],option['onePrice'],option['exchangeOnePrice'],this.lastPrice(option['prices'])]),addPrice:Number(option['addPrice']??0),minDay:Number(option['minDay']??0),maxDay:Number(option['maxDay']??0),minShipping:Number(option['minShipping']??0),maxShipping:Number(option['maxShipping']??0),shippingPeriod:String(option['shippingPeriod']??option['period']??option['shippingUnit']??'').trim().toLowerCase()})),
          items: listings.map((item) => ({
            cartID: Number(item['id'] ?? item['cartID'] ?? 0),
            sellerID: Number(sellerKey),
            sellerName: String(sellerInfo['shopName'] ?? sellerInfo['sellerName'] ?? group['sellerName'] ?? group['shopName'] ?? `Mağaza #${sellerKey}`),
            shippingID: Number(shippingKey),
            shippingName: String(group['shippingName'] ?? group['shippingTitle'] ?? `Gönderim profili #${shippingKey}`),
            listingID: Number(item['listingID'] ?? item['listingId'] ?? item['productID'] ?? 0),
            slug: String(item['slug'] ?? item['listingSlug'] ?? item['urlSlug'] ?? '') || undefined,
            versionID: Number(item['versionID'] ?? 0) || undefined,
            formAnswerID: Number(item['formAnswerID'] ?? 0) || undefined,
            listingType: String(item['listingType'] ?? ''),
            title: String(item['title'] ?? 'Ürün'),
            imageUrl: this.mediaUrl(item['url'] ?? item['imageUrl'] ?? item['image'] ?? item['listingImage'] ?? item['productImage']),
            quantity: Math.max(1, Number(item['quantity'] ?? 1)),
            price: this.cartCurrentPrice(item),
            originalPrice: this.cartOriginalPrice(item),
            currency: this.localePreference.currency() || String(item['currency'] ?? sellerInfo['currency'] ?? 'TRY'),
            preparationDays: Number(item['deliveryTime'] ?? item['preparationDays'] ?? 0) || 0,
            options: this.parseOptions(item['details'] ?? item['options'] ?? item['selectedOptions'])
            ,selectedOptions: this.parseSelectedOptions(item['details'] ?? item['options'] ?? item['selectedOptions'])
            ,customization: item['customization'] && typeof item['customization'] === 'object' ? item['customization'] as Record<string, unknown> : undefined
            ,campaign: (Array.isArray(item['campaigns'] ?? item['campaign'])
              ? this.asRecordList(item['campaigns'] ?? item['campaign'])
              : (item['campaign'] && typeof item['campaign'] === 'object' ? [item['campaign'] as Record<string, unknown>] : []))
              .map(campaign => ({
              id: Number(campaign['id'] ?? 0),
              title: String(campaign['title'] ?? ''),
              campaignType: String(campaign['campaignType'] ?? campaign['type'] ?? ''),
              discountType: campaign['discountType'] === null || campaign['discountType'] === undefined ? null : String(campaign['discountType']),
              discountValue: campaign['discountValue'] === null || campaign['discountValue'] === undefined ? null : Number(campaign['discountValue'])
            }))[0] ?? null
          })),
          preparationDays: Math.max(...listings.map(item => Number(item['deliveryTime'] ?? item['preparationDays'] ?? 0) || 0), 0)
        });
      }
    }
    const serverItems = groups.flatMap((group) => group.items);
    // The API is authoritative. A local visual item must never be promoted to
    // a checkout group: it has no server-calculated price or shipping route and
    // previously produced the misleading "country unavailable"/zero totals UI.
    const items = serverItems;
    const selectedShippingTotal=groups.reduce((total,group)=>total+(group.shippingOptions.find(option=>option.id===group.selectedShippingID)?.price ?? 0),0);
    const serverShipping=Number(prices['shippingCost'] ?? 0);
    const normalizedPrices: Record<string, unknown> = {
      ...prices,
      listingPrice: Number(prices['listingPrice'] ?? 0),
      shippingCost: selectedShippingTotal>0 ? selectedShippingTotal : serverShipping,
      totalAmount: selectedShippingTotal>0 ? Number(prices['totalAmount'] ?? 0) + selectedShippingTotal - serverShipping : Number(prices['totalAmount'] ?? 0)
    };
    this.items.set(items);
    this.persist();
    const rawCoupons = canonicalData?.['availableCoupons'] ?? apiData['availableCoupons'] ?? this.findValue(response, 'availableCoupons') ?? [];
    const availableCoupons = this.asRecordList(rawCoupons).map(item => ({
      id: Number(item['id'] ?? 0),
      title: String(item['title'] ?? 'Coupon'),
      code: String(item['code'] ?? item['couponCode'] ?? '').toUpperCase(),
      discountType: item['discountType'] == null ? null : String(item['discountType']),
      discountValue: item['discountValue'] == null ? null : Number(item['discountValue']),
      maxDiscount: item['maxDiscount'] == null ? null : Number(item['maxDiscount'])
    })).filter(item => item.code);
    const requestedCoupon=String(this.couponCode()||'').trim().toUpperCase();
    const serverCoupon=String(canonicalData?.['appliedCouponCode'] ?? apiData['appliedCouponCode'] ?? '').trim().toUpperCase();
    const appliedCouponCode=requestedCoupon && availableCoupons.some(coupon=>coupon.code===requestedCoupon)
      ? (serverCoupon || requestedCoupon)
      : '';
    return { items, groups, prices: normalizedPrices, country, availableCoupons, appliedCouponCode };
  }

  async applyCoupon(code: string): Promise<CommerceCartSnapshot> {
    this.couponCode.set(String(code || '').trim().toUpperCase());
    return this.load();
  }

  async remove(cartID: number): Promise<void> {
    await firstValueFrom(this.api.post('cartDelete', { id: cartID }));
    this.items.update((items) => items.filter((item) => item.cartID !== cartID));
    this.persist();
  }

  async saveForLater(cartID: number): Promise<void> {
    await firstValueFrom(this.api.post('cartSaveForLater', { id: cartID }));
    this.items.update((items) => items.filter((item) => item.cartID !== cartID));
    this.persist();
  }

  async savedForLater(): Promise<SavedCartItem[]> {
    const response = await firstValueFrom(this.api.post<Record<string, unknown>>('cartSavedForLater', {}));
    const value = this.findValue(response, 'savedCart') ?? this.findValue(response, 'savedCartItems') ?? [];
    return this.asRecordList(value).map((item) => ({
      id: Number(item['id'] ?? 0),
      listingID: Number(item['listingID'] ?? 0) || undefined,
      title: String(item['title'] ?? 'Product'),
      slug: String(item['slug'] ?? '') || undefined,
      imageUrl: this.mediaUrl(item['url'] ?? item['imageUrl'])
    })).filter((item) => item.id > 0);
  }

  async removeSavedForLater(id: number): Promise<void> {
    await firstValueFrom(this.api.post('cartSavedForLaterDelete', { id }));
  }

  async restoreSavedForLater(id: number): Promise<void> {
    await firstValueFrom(this.api.post('cartSavedForLaterRestore', { id }));
  }

  async updateQuantity(cartID: number, quantity: number): Promise<void> {
    await firstValueFrom(this.api.post('cartQuantity', { cartID, quantity: Math.max(1, quantity) }));
    this.items.update((items) => items.map((item) => item.cartID === cartID ? { ...item, quantity: Math.max(1, quantity) } : item));
    this.persist();
  }

  async selectShipping(sellerID:number,shippingID:number,selectedShippingID:number):Promise<void>{
    await firstValueFrom(this.api.post('cartShipping',{sellerID,shippingID,selectedShippingID,shippingProfileRouteID:selectedShippingID}));
  }

  async updateItem(item: CartVisualItem, options: Record<number, number>, customization: Record<string, unknown>): Promise<void> {
    await firstValueFrom(this.api.post('cartUpdate', {
      cartID: item.cartID,
      listingID: item.listingID,
      quantity: item.quantity,
      options,
      customization
    }));
  }

  remember(item: CartVisualItem): void {
    const normalizedItem={...item,imageUrl:this.mediaUrl(item.imageUrl)};
    const current = [...this.items()];
    const existing = current.find((entry) => entry.listingID === item.listingID);
    if (existing) {
      existing.quantity += item.quantity;
      existing.title=normalizedItem.title||existing.title;
      existing.imageUrl=normalizedItem.imageUrl||existing.imageUrl;
    } else {
      current.push(normalizedItem);
    }
    this.items.set(current);
    this.lastAdded.set(normalizedItem);
    if (typeof window !== 'undefined') {
      this.persist();
      window.setTimeout(() => this.lastAdded.set(null), 2400);
    }
  }

  animateToCart(source: HTMLElement | null): void {
    if (typeof document === 'undefined' || !source) {
      return;
    }
    const image = source.querySelector('img') ?? source.closest('.detail-hero')?.querySelector('img');
    const target = document.querySelector<HTMLElement>('[data-cart-target]');
    if (!image || !target) {
      return;
    }
    const from = image.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    const clone = image.cloneNode(true) as HTMLImageElement;
    Object.assign(clone.style, {
      borderRadius: '24px',
      height: `${Math.min(from.height, 180)}px`,
      left: `${from.left}px`,
      objectFit: 'cover',
      pointerEvents: 'none',
      position: 'fixed',
      top: `${from.top}px`,
      width: `${Math.min(from.width, 180)}px`,
      zIndex: '9999'
    });
    document.body.appendChild(clone);
    clone.animate([
      { opacity: 1, transform: 'translate(0,0) scale(1) rotate(0deg)' },
      { opacity: .96, offset: .45, transform: `translate(${(to.left - from.left) * .52}px,${(to.top - from.top) * .42}px) scale(.62) rotate(-4deg)` },
      { opacity: .2, transform: `translate(${to.left - from.left}px,${to.top - from.top}px) scale(.12) rotate(8deg)` }
    ], { duration: 820, easing: 'cubic-bezier(.16,.84,.24,1)' }).finished.finally(() => clone.remove());
    target.animate([
      { transform: 'scale(1)' },
      { transform: 'scale(1.18)', offset: .72 },
      { transform: 'scale(1)' }
    ], { duration: 980, easing: 'cubic-bezier(.2,.9,.2,1)' });
  }

  private restore(): CartVisualItem[] {
    if (typeof window === 'undefined') {
      return [];
    }
    try {
      const parsed = JSON.parse(window.localStorage.getItem('aymudo.cartVisual') || '[]');
      return Array.isArray(parsed) ? parsed.map((item)=>({...item,imageUrl:this.mediaUrl(item?.imageUrl)})) : [];
    } catch {
      return [];
    }
  }

  private persist(): void {
    if (typeof window !== 'undefined') window.localStorage.setItem('aymudo.cartVisual', JSON.stringify(this.items()));
  }

  private mediaUrl(value: unknown): string | null {
    const url = String(value ?? '').replace(/\[size\]/g, 's').trim();
    if (!url) return null;
    const mediaBase=String(this.site.config().mediaBaseUrl||this.site.config().apiBaseUrl||'').replace(/\/+$/,'');
    try{
      const parsed=new URL(url,mediaBase||'https://invalid.local');
      if(/^\/media\//i.test(parsed.pathname)&&mediaBase){
        return `${mediaBase}${parsed.pathname}${parsed.search}`;
      }
    }catch{}
    if(/^https?:\/\//i.test(url))return url;
    if(/^\/?media\//i.test(url)&&mediaBase)return `${mediaBase}/${url.replace(/^\/+/, '')}`;
    return url.startsWith('/') ? url : `/${url}`;
  }

  private parseOptions(value: unknown): Array<{ name: string; value: string; unit?: string }> {
    return this.asRecordList(value).map((option) => {
      const item = option && typeof option === 'object' ? option as Record<string, unknown> : {};
      const name = String(item['option'] ?? item['optionName'] ?? item['featureName'] ?? item['attributeName'] ?? item['name'] ?? item['title'] ?? '').trim();
      const selected = String(item['subOption'] ?? item['subOptionName'] ?? item['value'] ?? item['selectedValue'] ?? '').trim();
      const unit = String(item['unit'] ?? item['unitName'] ?? '').trim();
      return { name: name || (selected ? 'Bed size' : ''), value: selected, ...(unit ? { unit } : {}) };
    }).filter((option) => option.name || option.value);
  }

  private parseSelectedOptions(value: unknown): Record<number, number> {
    const selected: Record<number, number> = {};
    for (const option of this.asRecordList(value)) {
      const optionID = Number(option['optionID'] ?? option['id'] ?? 0);
      const subOptionID = Number(option['subOptionID'] ?? option['subID'] ?? option['valueID'] ?? 0);
      if (optionID > 0 && subOptionID > 0) selected[optionID] = subOptionID;
    }
    return selected;
  }

  private lastPrice(value: unknown): number {
    const prices = this.asRecordList(value)
      .map(item => Number(item['price'] ?? item['amount'] ?? 0))
      .filter(price => Number.isFinite(price));
    return prices.length ? prices[prices.length - 1] : 0;
  }

  private firstPositive(values: unknown[]): number {
    for (const value of values) {
      const number = Number(value ?? 0);
      if (Number.isFinite(number) && number > 0) return number;
    }
    return 0;
  }

  private campaignOriginalPrice(item: Record<string, unknown>): number {
    const campaign = Array.isArray(item['campaign'] ?? item['campaigns'])
      ? this.asRecordList(item['campaign'] ?? item['campaigns'])[0]
      : item['campaign'] && typeof item['campaign'] === 'object' ? item['campaign'] as Record<string, unknown> : null;
    if(!campaign || typeof campaign!=='object') return 0;
    const discountType=String(campaign['discountType'] ?? campaign['type'] ?? '').toLowerCase();
    const discount=Number(campaign['discountValue'] ?? campaign['discount'] ?? campaign['value'] ?? 0);
    const current=this.cartCurrentPrice(item);
    if(!(current>0&&discount>0)) return 0;
    const original = discountType==='percent' ? current/(1-discount/100) : current+discount;
    return Number.isFinite(original) && original > current ? original : 0;
  }

  private cartCurrentPrice(item: Record<string, unknown>): number {
    const quantity = Math.max(1, Number(item['quantity'] ?? 1));
    return this.firstPositive([
      item['discountedPrice'],
      item['totalDiscountedPrice'] ? Number(item['totalDiscountedPrice']) / quantity : 0,
      item['totalLastPrice'] ? Number(item['totalLastPrice']) / quantity : 0,
      item['price'],
      item['listingPrice'],
      item['totalPrice'] ? Number(item['totalPrice']) / quantity : 0,
      this.lastPrice(item['prices'])
    ]);
  }

  private cartOriginalPrice(item: Record<string, unknown>): number {
    const current = this.cartCurrentPrice(item);
    const quantity = Math.max(1, Number(item['quantity'] ?? 1));
    const explicit = this.firstPositive([
      item['originalPrice'],
      item['regularPrice'],
      item['compareAtPrice'],
      item['totalOriginalPrice'] ? Number(item['totalOriginalPrice']) / quantity : 0
    ]);
    if (explicit > current) return explicit;
    const campaignOriginal = this.campaignOriginalPrice(item);
    if (campaignOriginal > current) return campaignOriginal;
    return 0;
  }

  private asRecordList(value: unknown): Array<Record<string, unknown>> {
    if (Array.isArray(value)) return value.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');
    if (value && typeof value === 'object') return Object.values(value as Record<string, unknown>).filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');
    return [];
  }

  private findObject(value: unknown, key: string): Record<string, unknown> | null {
    const found = this.findValue(value, key);
    return found && typeof found === 'object' && !Array.isArray(found) ? found as Record<string, unknown> : null;
  }

  private findValue(value: unknown, key: string): unknown {
    if (!value || typeof value !== 'object') return undefined;
    if (!Array.isArray(value) && key in (value as Record<string, unknown>)) return (value as Record<string, unknown>)[key];
    for (const child of Object.values(value as Record<string, unknown>)) {
      const found = this.findValue(child, key);
      if (found !== undefined) return found;
    }
    return undefined;
  }

}
